import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useBeforeunload } from 'react-beforeunload';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import LayersOutlineIcon from 'mdi-react/LayersOutlineIcon';
import notify from '../../core/components/notify/notify.component';
import type { Coordinate, EditableFloorPlan, EditorFeature } from './models/editor-feature';
import {
  isDrawingTool, isPlacementTool, needsScale,
  type DrawTarget, type EditorTool,
} from './models/editor-tool';
import EditorInspector from './components/editor-inspector.component';
import EditorTopBar from './components/editor-top-bar.component';
import EditorStatusBar from './components/editor-status-bar.component';
import EditorToolRail from './components/editor-tool-rail.component';
import FloorplanCanvas from './components/floorplan-canvas.component';
import EditorLeftRail, { type LeftRailTab } from './components/editor-left-rail.component';
import LayersPanel from './components/layers-panel.component';
import HistoryPanel from './components/history-panel.component';
import FindPanel from './components/find-panel.component';
import Minimap from './components/minimap.component';
import CommandPalette from './components/command-palette.component';
import CanvasContextMenu from './components/canvas-context-menu.component';
import AutomatePanel from './components/automate-panel.component';
import ValidatePanel from './components/validate-panel.component';
import ImportPanel from './components/import-panel.component';
import FloorPlanStarter from './components/floorplan-starter.component';
import SimpleToolbox from './components/simple-toolbox.component';
import ModePanel from './components/mode-panel.component';
import type { BasicShapeTool } from './components/basic-shapes';
import {
  acceptsFeature, dimmedLayersOf, isAreaMode, modeCursorOf, polygonLayerOf, type EditMode,
} from './models/edit-mode';
import type { ReshapeMode } from './hooks/use-canvas-interaction';
import { EDITOR_FEATURES } from './utils/editor-features';
import { DEFAULT_SNAP_SETTINGS, gridSpacingOf, type SnapSettings } from './utils/snapping';
import SnapControls from './components/snap-controls.component';
import { useFloorPlanAutomation } from './hooks/use-floorplan-automation';
import type { EditorCommand } from './models/editor-command';
import {
  DEFAULT_LAYER_SETTINGS,
  isFiltered,
  nextSolo,
  visibilityOf,
  withVisible,
  type EditorLayerKey,
  type LayerSettings,
} from './utils/layer-settings';
import ConfirmDialog from './components/confirm-dialog.component';
import DraftBanner from './components/draft-banner.component';
import SchedulePublishDialog from './components/schedule-publish-dialog.component';
import RemoveFloorPlanDialog from './components/remove-floorplan-dialog.component';
import ChangeStoreyDialog from './components/change-storey-dialog.component';
import { useFloorPlanDraft } from './hooks/use-floorplan-draft';
import {
  cancelFloorPlanDraftSchedule, discardFloorPlanDraft, type FloorPlanDraft,
} from './services/floorplan-editor.service';
import IconPicker from './components/icon-picker.component';
import DeskGroupPicker from './components/desk-group-picker.component';
import {
  deskGroupSeats,
  DEFAULT_GROUP_OPTIONS,
  DESK_GROUP_SHAPES,
  type DeskGroupKind,
  type DeskGroupShape,
} from './utils/desk-groups';
import {
  DEFAULT_FOOTPRINT, deskShapeRing, LEGACY_TEMPLATE, type DeskFootprint,
} from './utils/desk-symbols';
import RoomPickerDialog from './components/room-picker-dialog.component';
import DeleteDesksDialog from './components/delete-desks-dialog.component';
import { bookableDeskCountOf, canDeleteSelection } from './utils/deletion-guard';
import { NO_DESK_ICONS, type DeskIcons } from './utils/mutations';
import ShortcutsDialog from './components/shortcuts-dialog.component';
import ArrayDialog from './components/array-dialog.component';
import BulkActions from './components/bulk-actions.component';
import {
  alignOffsets, arrayOffsets, distributeOffsets, duplicateOffsetOf,
  type AlignEdge, type ArraySpec, type DistributeAxis,
} from './utils/arrangement';
import EditorZoomBar from './components/editor-zoom-bar.component';
import { editorTheme, sectionHeadingSx } from './utils/editor-theme';
import { useDrawing } from './hooks/use-drawing';
import { formatArea, formatLength, isUsableScale, metresToUnits } from './utils/measurement';
import { estimateScale } from './utils/scale-estimate';
import { computeBoundingBox, createProjection, projectedExtentOf } from './utils/projection';
import { boundsOf } from './utils/selection-chrome';
import { featuresOnLayer, selectionKeyOf } from './utils/selection';
import { arrangeCommands } from './utils/arrange-commands';
import { outlinesOfDesks } from './utils/pod-layout';
import { useEditorDocument } from './hooks/use-editor-document';
import { useEditorShortcuts } from './hooks/use-editor-shortcuts';
import { useFloorPlanEditor } from './hooks/use-floorplan-editor';
import { withDraft } from './utils/draft-overlay';
import { useKeycloak } from '../authentication/keycloak/KeycloakProvider';
import { useFloorPlanRooms } from './hooks/use-floorplan-rooms';
import { useOfficeName } from './hooks/use-office-name';
import { useNudge } from './hooks/use-nudge';
import { useSaveFloorPlan } from './hooks/use-save-floorplan';
import { useSelection } from './hooks/use-selection';
import { useViewport } from './hooks/use-viewport';
import {
  applyRoomAssignments, appliedRetirementIdsOf, bookableFeatureIdsOf, retirementDatesOf,
} from './utils/room-assignments';
import { officeTodayFrom, retiredDeskIdsAsOf } from './utils/retired-desks';
import { useWorkplaceRetirement } from './hooks/use-workplace-retirement';
import { roomIdsInUse } from './utils/room-groups';
import { capacityImpactOf, pendingCapacityDeltas, type RoomCapacityImpact } from './utils/capacity-impact';

/** The surface's own coordinate space. Fixed, with the browser scaling the SVG to fit. */
const SURFACE_WIDTH = 1200;
const SURFACE_HEIGHT = 800;

/** How thick a new line on the floor is drawn: a light partition, easy to see and to click. */
const DEFAULT_LINE_THICKNESS_METRES = 0.1;

/**
 * The floorplan editor.
 *
 * A desktop admin tool: it needs a canvas, a tool rail and a properties panel side by side,
 * which does not survive being folded into a phone-width column. Below roughly 1200px the
 * inspector moves under the canvas rather than pretending otherwise.
 */
const FloorPlanEditor = (): React.JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: officeId } = useParams<{ id: string }>();

  const [storey, setStorey] = useState<number | undefined>();
  const [tool, setTool] = useState<EditorTool>('select');
  // What the admin is working on — rooms, the floor, the outline — which narrows what a click
  // can reach. Separate from the tool: the tool is what the next click does, the mode is what
  // it may land on, and "Rooms" with "draw a rectangle" armed is both at once.
  const [mode, setMode] = useState<EditMode>('select');
  // How a press on the selected area's outline is read: move it whole, drag its corners, or
  // push and pull its sides.
  const [reshape, setReshape] = useState<ReshapeMode>('off');
  // The icon tool picks its glyph before placing it, so the click on the plan only has to
  // say where it goes.
  const [pendingIconName, setPendingIconName] = useState<string | undefined>();
  const [iconPickerAnchor, setIconPickerAnchor] = useState<HTMLElement | null>(null);
  // Where a desk is about to go, while its room is being chosen. The desk itself is not
  // created until then: a desk placed first and cancelled second would leave exactly the
  // unbookable seat the dialog exists to prevent.
  const [pendingDeskAt, setPendingDeskAt] = useState<Coordinate | undefined>();
  // A whole arrangement waiting for its room. Asked once for the group rather than once per
  // seat: a bench of six is one thing to the person placing it.
  const [pendingGroupAt, setPendingGroupAt] = useState<Coordinate | undefined>();
  // What every newly created desk and furniture outline is drawn as. One setting rather than
  // a property to set forty times: an office whose desks are all phone booths says so once.
  const [deskIcons, setDeskIcons] = useState<DeskIcons>(NO_DESK_ICONS);
  const [groupKind, setGroupKind] = useState<DeskGroupKind>('bench4');
  // Rows and columns for a custom arrangement, which the presets deliberately do not cover:
  // a row of eight and two benches of four are both eight desks and are not the same floor.
  const [groupCustom, setGroupCustom] = useState<DeskGroupShape>(DESK_GROUP_SHAPES.custom);
  const [groupFootprint, setGroupFootprint] = useState<DeskFootprint>(DEFAULT_FOOTPRINT);
  const [groupRotation, setGroupRotation] = useState(DEFAULT_GROUP_OPTIONS.rotationDegrees);
  // How far apart a stamped group's desks sit, in metres. Editable before placing: the right
  // gap is a property of the office, not of this editor.
  const [groupGap, setGroupGap] = useState(DEFAULT_GROUP_OPTIONS.gapMetres);
  const [groupAisle, setGroupAisle] = useState(DEFAULT_GROUP_OPTIONS.aisleMetres);
  const [groupBackGap, setGroupBackGap] = useState(DEFAULT_GROUP_OPTIONS.backGapMetres);
  const [groupPickerAnchor, setGroupPickerAnchor] = useState<HTMLElement | null>(null);
  // The capacity each room would lose, while the admin decides whether to go ahead.
  const [pendingDeletion, setPendingDeletion] = useState<RoomCapacityImpact[] | undefined>();
  // Which layers are drawn and how strongly, and which one is being worked on alone. A view
  // setting throughout: the saved document always holds every feature it loaded.
  const [layerSettings, setLayerSettings] = useState<LayerSettings>(DEFAULT_LAYER_SETTINGS);
  const [soloLayer, setSoloLayer] = useState<EditorLayerKey | null>(null);
  // Which accordion section is open, or null when they are all closed and the column belongs
  // to the canvas.
  const [leftTab, setLeftTab] = useState<LeftRailTab | null>('layers');
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Where the context menu is, and what it was opened on. Undefined while it is closed.
  const [contextMenu, setContextMenu] = useState<
    { position: { x: number; y: number }; feature: EditorFeature | undefined } | undefined
  >();
  // The background ruling. On by default: it is a fixed backdrop rather than anything the
  // drawing has to line up with, and a working surface with nothing behind it reads as a void.
  const [showGrid, setShowGrid] = useState(true);
  // Off until chosen: a pointer that quietly moves where it was put reads as a broken mouse to
  // anybody who did not ask for it.
  const [snapSettings, setSnapSettings] = useState<SnapSettings>(DEFAULT_SNAP_SETTINGS);
  // Which of the three polygon layers a drawn area lands on. The tool says what shape; this
  // says where, because the same rectangle is a room on one layer and a wall on another.
  const [drawTarget, setDrawTarget] = useState<DrawTarget>('room');
  // Where the pointer is on the plan, for the status bar. Held here rather than in the canvas
  // because the bar is a sibling of it, not a child.
  const [pointerAt, setPointerAt] = useState<Coordinate | undefined>();
  const pointerFrame = useRef<number | undefined>();
  const [arrayOpen, setArrayOpen] = useState(false);
  const toolRailRef = useRef<HTMLDivElement>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // What to do once the admin confirms losing their unsaved changes. Held rather than run
  // immediately, because the dialog answers asynchronously.
  const [pendingConfirmation, setPendingConfirmation] = useState<{ messageKey: string; act:() => void } | undefined>();

  const {
    loading,
    error,
    storeys,
    floorPlan: liveFloorPlan,
    stored: storedFloorPlan,
    refresh: refreshFloorPlan,
  } = useFloorPlanEditor({ officeId, storey });

  const keycloak = useKeycloak();

  // Unpublished changes, when the plan has them: the editor opens on the draft rather than on
  // what is live, and says so for as long as it is.
  const draftState = useFloorPlanDraft(liveFloorPlan?.floorPlanId);
  const { loadedDraft } = draftState;

  const loadedFloorPlan = useMemo(() => (
    liveFloorPlan && storedFloorPlan && loadedDraft && loadedDraft.floorPlanId === liveFloorPlan.floorPlanId
      ? withDraft(storedFloorPlan, loadedDraft)
      : liveFloorPlan
  ), [liveFloorPlan, storedFloorPlan, loadedDraft]);

  // A new floor being started while the office already has others, and on which storey.
  const [startingFloor, setStartingFloor] = useState<number | undefined>();
  const [changeStoreyOpen, setChangeStoreyOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);
  const officeName = useOfficeName(officeId);
  const {
    loading: roomsLoading,
    rooms,
    assignments,
    planPeriodDays,
    earliestRetirementDate,
    error: roomsError,
    refresh: refreshRooms,
  } = useFloorPlanRooms(loadedFloorPlan?.floorPlanId);

  // The plan as loaded says nothing reliable about which room a desk is in — booking reads
  // the Workplaces table, and the stored roomId is only an echo of the last save.
  //
  // Its identity is what reloads the editor's document, so it must change only when the data
  // behind it does. Deriving it from a loading flag as well would hand the document a new
  // object mid-refresh, and the document would reset itself to whatever was fetched last —
  // which, right after a save, is the plan as it was *before* the save.
  //
  // Recomputed in an effect rather than a memo so it can be held still while a fetch is in
  // flight. A save refreshes the plan and the room assignments separately, and whichever
  // lands first would otherwise rebuild this from one new half and one stale half — briefly
  // reloading the editor with the plan as it was before the save.
  const [floorPlanWithRooms, setFloorPlanWithRooms] = useState<EditableFloorPlan | undefined>();

  useEffect(() => {
    if (loading || roomsLoading || draftState.loading) {
      return;
    }

    setFloorPlanWithRooms(loadedFloorPlan
      ? applyRoomAssignments(loadedFloorPlan, assignments, { keepDrawnRooms: !!loadedDraft })
      : undefined);
  }, [loadedFloorPlan, assignments, loading, roomsLoading, draftState.loading, loadedDraft]);

  const editorDocument = useEditorDocument(floorPlanWithRooms, deskIcons);
  const selection = useSelection(editorDocument.floorPlan);
  const viewport = useViewport(SURFACE_WIDTH, SURFACE_HEIGHT);

  const onSaved = useCallback((): void => {
    editorDocument.markSaved();
    // Published: a draft that was being edited is gone, and the live plan is what to show.
    draftState.refresh();

    // Both halves have to be re-read, and the plan is the one that matters: the server
    // normalises what it stores and writes each desk's resolved room back into it, so the
    // copy loaded on mount no longer matches the database. Refreshing only the rooms used to
    // reload the document from that stale copy, which made a desk that had just been saved
    // vanish from the canvas until the page was reloaded.
    refreshFloorPlan();
    refreshRooms();
  }, [editorDocument, refreshFloorPlan, refreshRooms, draftState]);

  const onDraftSaved = useCallback((draft: FloorPlanDraft | null): void => {
    editorDocument.markSaved();
    draftState.replace(draft);
  }, [editorDocument, draftState]);

  const {
    saving, issues, save, saveDraft, schedule, exportPlan,
  } = useSaveFloorPlan({
    floorPlan: editorDocument.floorPlan,
    dirty: editorDocument.dirty,
    draft: draftState.draft,
    onSaved,
    onDraftSaved,
  });

  /** Throws the draft away and goes back to the live plan. */
  const discardDraft = useCallback(async (): Promise<void> => {
    if (!liveFloorPlan) return;

    setDraftBusy(true);
    try {
      if (await discardFloorPlanDraft(liveFloorPlan.floorPlanId, keycloak)) {
        notify(String(t('floorplan-editor-draft-discarded')), true);
        draftState.refresh();
      } else {
        notify(String(t('floorplan-editor-save-failed')));
      }
    } finally {
      setDraftBusy(false);
    }
  }, [liveFloorPlan, keycloak, draftState, t]);

  const cancelSchedule = useCallback(async (): Promise<void> => {
    if (!liveFloorPlan) return;

    setDraftBusy(true);
    try {
      if (await cancelFloorPlanDraftSchedule(liveFloorPlan.floorPlanId, keycloak)) {
        draftState.refresh();
      } else {
        notify(String(t('floorplan-editor-save-failed')));
      }
    } finally {
      setDraftBusy(false);
    }
  }, [liveFloorPlan, keycloak, draftState, t]);

  // Declared with the document rather than beside the things that read it: the placement
  // handlers need it, and every desk dimension in this editor is in metres.
  const storedScale = editorDocument.floorPlan?.unitsPerMetre ?? null;

  /**
   * A scale worked out from the drawing itself, for the plans nobody has measured — which is
   * every stored plan.
   *
   * Used as the working scale and never written: the document keeps its null until an
   * administrator measures something or accepts the estimate in the Scale panel. That
   * distinction is the whole point — the tool works on first open, and the saved data still
   * only ever carries a figure somebody stood behind.
   */
  const scaleEstimate = useMemo(
    () => (storedScale === null ? estimateScale(editorDocument.floorPlan) : null),
    [storedScale, editorDocument.floorPlan],
  );

  const scale = storedScale ?? scaleEstimate?.unitsPerMetre ?? null;

  // The plan's width in its own units, which is what a nudge step is a fraction of.
  const planWidth = useMemo(() => {
    if (!editorDocument.floorPlan) {
      return 1;
    }

    const box = computeBoundingBox(editorDocument.floorPlan);
    return box.maxX - box.minX;
  }, [editorDocument.floorPlan]);

  // The browser's own guard, for a tab close or a reload. In-app navigation is guarded
  // separately, below, because React Router never triggers this one.
  useBeforeunload((event: BeforeUnloadEvent) => {
    if (editorDocument.dirty) {
      event.preventDefault();
    }
  });

  /** Runs an action, asking first when it would throw away unsaved edits. */
  const guardUnsaved = useCallback(
    (messageKey: string, act: () => void): void => {
      if (!editorDocument.dirty) {
        act();
        return;
      }

      setPendingConfirmation({ messageKey, act });
    },
    [editorDocument.dirty],
  );

  const place = useCallback(
    (at: Coordinate): void => {
      let created: string | undefined;

      if (tool === 'desk') {
        // A desk is only bookable once it sits in a room, and the room cannot be inferred:
        // rooms overlap on a plan, and a desk drawn inside a room's outline is not
        // necessarily meant to belong to it. Asking now costs one click; discovering later
        // that a floor of desks cannot be reserved costs a support ticket.
        setPendingDeskAt(at);
        setTool('select');
        return;
      }

      if (tool === 'deskGroup') {
        // Same reasoning as a single desk: the room cannot be inferred, and a bench of six
        // placed into no room is six seats nobody can book.
        setPendingGroupAt(at);
        setTool('select');
        return;
      }

      if (tool === 'deskShape') {
        // Drawn as the symbol and size chosen for desk groups, so a furniture outline placed
        // on its own matches the ones a group stamps. Without a scale there is no size in
        // metres to draw it at, and the legacy template is used instead.
        created = editorDocument.createDeskShape(
          at,
          isUsableScale(scale) ? deskShapeRing(at, groupFootprint, scale) : undefined,
        );
      } else if (tool === 'text') {
        // Placed empty and selected, so the text is typed into the inspector rather than
        // into a modal that has to be dismissed before the label can even be seen.
        created = editorDocument.createTextLabel(at, '');
      } else if (tool === 'icon' && pendingIconName) {
        created = editorDocument.createIconLabel(at, pendingIconName);
      }

      if (created) {
        selection.selectKeys([created]);
      }

      // Back to Select once something is placed: a tool that stays armed turns the next
      // click, meant as a selection, into another desk.
      setTool('select');
      setPendingIconName(undefined);
    },
    [tool, pendingIconName, editorDocument, selection, groupFootprint, scale],
  );

  /** Places the desk the room dialog was opened for, in the room the admin chose. */
  const placeDeskInRoom = useCallback(
    (roomId: string): void => {
      if (!pendingDeskAt) {
        return;
      }

      // With its furniture, always: the outline at the size desk groups use, or the outline the
      // editor has always drawn on a plan with no scale to size one by.
      const ring = isUsableScale(scale)
        ? deskShapeRing(pendingDeskAt, groupFootprint, scale)
        : LEGACY_TEMPLATE.map(([x, y]): Coordinate => [pendingDeskAt[0] + x, pendingDeskAt[1] + y]);

      const created = editorDocument.createDesk(pendingDeskAt, roomId, ring);

      if (created) {
        selection.selectKeys([created]);
      }

      setPendingDeskAt(undefined);
    },
    [pendingDeskAt, editorDocument, selection, scale, groupFootprint],
  );

  /** Stamps the arrangement the picker describes, in the room the admin chose. */
  const placeDeskGroupInRoom = useCallback(
    (roomId: string): void => {
      if (!pendingGroupAt) {
        return;
      }

      const seats = deskGroupSeats(
        pendingGroupAt,
        {
          kind: groupKind,
          custom: groupCustom,
          footprint: groupFootprint,
          gapMetres: groupGap,
          aisleMetres: groupAisle,
          backGapMetres: groupBackGap,
          rotationDegrees: groupRotation,
        },
        // The scale the rest of the editor works from, which is the estimate on every stored
        // plan. Reading the *document's* figure here — null on all of them — meant the layout
        // came back empty and the group was silently never placed.
        scale,
      );

      const created = editorDocument.createDeskGroup(seats, roomId);

      if (created.length) {
        selection.selectKeys(created);
        notify(String(t('floorplan-editor-desk-group-placed', { count: created.length })), true);
      } else {
        // Never silence. A placement that produces nothing is a bug or an unmeasurable plan,
        // and either way the admin clicked and deserves to be told it did not happen.
        notify(String(t('floorplan-editor-desk-group-failed')), false);
      }

      setPendingGroupAt(undefined);
    },
    [
      pendingGroupAt,
      groupKind,
      groupCustom,
      groupFootprint,
      groupRotation,
      groupGap,
      groupAisle,
      groupBackGap,
      scale,
      editorDocument,
      selection,
      t,
    ],
  );

  /**
   * The rooms this storey's desks already sit in, including desks placed but not yet saved.
   *
   * Without this, a room only counts as being on a floor once its desks have reached the
   * database — so the room an admin picked a moment ago would drop out of the list while they
   * were still placing the rest of the block.
   */
  const roomIdsOnThisFloor = useMemo(
    () => roomIdsInUse(editorDocument.floorPlan?.desks ?? []),
    [editorDocument.floorPlan],
  );

  const retirementDates = useMemo(() => retirementDatesOf(assignments), [assignments]);

  // Already carried out, as opposed to merely scheduled. The server refuses to reschedule or
  // cancel these, so the editor stops offering either.
  const appliedRetirementIds = useMemo(() => appliedRetirementIdsOf(assignments), [assignments]);

  // Today at the office, recovered from the server's own boundary rather than the device clock:
  // the same desk is retired in Amsterdam and not yet in Auckland, and the browser cannot tell.
  const officeToday = useMemo(
    () => officeTodayFrom(earliestRetirementDate, planPeriodDays),
    [earliestRetirementDate, planPeriodDays],
  );

  // Retired, not retiring. A desk scheduled for next month is still bookable and stays drawn.
  const retiredDeskIds = useMemo(
    () => retiredDeskIdsAsOf(retirementDates, officeToday),
    [retirementDates, officeToday],
  );

  /**
   * Selects everything on a layer, from the layers panel.
   *
   * Shows the layer first when it is hidden. Hit-testing ignores anything that is not drawn —
   * deliberately, so a click cannot land on a feature nobody can see — and a selection of
   * invisible features would be one the admin can neither see nor click away from.
   */
  const selectLayer = useCallback(
    (key: EditorLayerKey): void => {
      if (!editorDocument.floorPlan) {
        return;
      }

      setLayerSettings((current) => withVisible(current, key, true));
      setSoloLayer((current) => (current !== null && current !== key ? null : current));

      selection.selectKeys(
        featuresOnLayer(editorDocument.floorPlan, key, retiredDeskIds).map(selectionKeyOf),
      );
    },
    [editorDocument.floorPlan, retiredDeskIds, selection],
  );

  const retirement = useWorkplaceRetirement();
  const automation = useFloorPlanAutomation(editorDocument.floorPlan?.floorPlanId);

  // What is actually painted, once solo has had its say. Computed rather than stored, so the
  // admin's own visibility choices survive a solo being turned on and off again.
  const layerVisibility = useMemo(() => visibilityOf(layerSettings, soloLayer), [layerSettings, soloLayer]);
  const layersFiltered = useMemo(() => isFiltered(layerSettings, soloLayer), [layerSettings, soloLayer]);

  /**
   * Aligning, distributing, copying.
   *
   * All of them resolve the selection against the current document and hand the result to a
   * single mutation, so each is one undo entry and none of them can drift from what a drag
   * does. The offsets are computed by `arrangement`, which knows nothing about React.
   */
  /**
   * Whether lining things up is offered for what is selected.
   *
   * Not for desks or their images. Those are placed by the desk tools, spaced by the group
   * panel and nudged by hand, and an edge alignment across them moves bookable seats by the
   * floor-load behind a single icon — at a spacing none of the tools that put them there would
   * have chosen. Rooms, walls and labels still align: for those it is the quickest way to tidy
   * a drawing, and nothing hangs off where they sit.
   */
  const alignable = useMemo(
    () => selection.features.length > 0
      && selection.features.every((feature) => feature.kind !== 'desk' && feature.kind !== 'deskShape'),
    [selection.features],
  );

  const align = useCallback((edge: AlignEdge): void => {
    editorDocument.moveEach(alignOffsets(selection.features, edge));
  }, [editorDocument, selection.features]);

  const distribute = useCallback((axis: DistributeAxis): void => {
    editorDocument.moveEach(distributeOffsets(selection.features, axis));
  }, [editorDocument, selection.features]);

  const duplicate = useCallback((offsets: readonly Coordinate[]): void => {
    const created = editorDocument.duplicate(selection.keys, offsets);

    // Selecting the copies rather than the originals: the next thing anybody does after
    // duplicating is move what they just made.
    if (created.length) {
      selection.selectKeys(created);
    }
  }, [editorDocument, selection]);

  /**
   * Accepting what the automation found.
   *
   * Each goes through the document, so it is one undo step and is written by the save path
   * that already moves a seat of room capacity with a desk. None of them writes on its own.
   */
  const placeDetectedDesks = useCallback((): void => {
    const placements = (automation.suggestions?.desks ?? [])
      // Only the ones drawn inside a room. A desk with no room is drawn and unbookable,
      // which is the state the detector exists to clear up rather than to create more of.
      .filter((desk) => desk.suggestedRoomId !== null)
      .map((desk) => ({ at: [desk.x, desk.y] as Coordinate, roomId: desk.suggestedRoomId as string }));

    const created = editorDocument.createDesks(placements);

    if (created.length) {
      selection.selectKeys(created);
      notify(String(t('floorplan-editor-automate-desks-placed', { count: created.length })), true);
    }
  }, [automation.suggestions, editorDocument, selection, t]);

  const drawSuggestedRooms = useCallback((): void => {
    const created = editorDocument.createRoomZones(
      (automation.suggestions?.rooms ?? []).map((room) => room.ring),
    );

    if (created.length) {
      selection.selectKeys(created);
      notify(String(t('floorplan-editor-automate-rooms-drawn', { count: created.length })), true);
    }
  }, [automation.suggestions, editorDocument, selection, t]);

  const applyRelink = useCallback((): void => {
    const roomsByFeatureId = new Map<string, string>();

    (automation.suggestions?.workplaceRooms ?? []).forEach((proposal) => {
      if (proposal.suggestedRoomId) {
        roomsByFeatureId.set(proposal.workplaceId, proposal.suggestedRoomId);
      }
    });

    if (roomsByFeatureId.size) {
      editorDocument.assignDeskRooms(roomsByFeatureId);
      notify(String(t('floorplan-editor-automate-relinked', { count: roomsByFeatureId.size })), true);
    }
  }, [automation.suggestions, editorDocument, t]);

  const applyCleaning = useCallback((): void => {
    automation.applyCleaning().then((changed) => {
      if (!changed) {
        return;
      }

      // Cleaning rewrote the stored layers, so the copy on screen is out of date — and it is
      // the copy the next save would be made against. Re-reading is what stops that save
      // putting the empty geometry back.
      notify(String(t('floorplan-editor-automate-clean-done')), true);
      refreshFloorPlan();
      refreshRooms();
    }).catch(() => undefined);
  }, [automation, refreshFloorPlan, refreshRooms, t]);

  /**
   * The pointer's position, at most once a frame.
   *
   * This state lives at the root of the editor, so every update re-renders the rail, the
   * inspector and the panels beside the canvas. A pointer move fires far more often than
   * anybody can read a coordinate, and setting it per move made every mouse move over the
   * drawing a full render of everything around it.
   */
  const reportPointer = useCallback((at: Coordinate | undefined): void => {
    if (pointerFrame.current !== undefined) {
      cancelAnimationFrame(pointerFrame.current);
    }

    pointerFrame.current = requestAnimationFrame(() => {
      pointerFrame.current = undefined;
      setPointerAt(at);
    });
  }, []);

  useEffect(() => () => {
    if (pointerFrame.current !== undefined) {
      cancelAnimationFrame(pointerFrame.current);
    }
  }, []);

  // A plan with nothing on any layer. Counted from the document rather than from the fetch,
  // so it disappears the moment the first thing is drawn.
  const planIsEmpty = useMemo(() => {
    const plan = editorDocument.floorPlan;

    if (!plan) {
      return false;
    }

    return plan.border.length + plan.rooms.length + plan.floorBackdrop.length + plan.floorLines.length
      + plan.desks.length + plan.deskShapes.length + plan.labels.length === 0;
  }, [editorDocument.floorPlan]);

  // The same projection the canvas builds, so a snapped point lands where the pointer is.
  const projection = useMemo(
    () => {
      const plan = editorDocument.floorPlan;

      // An empty extent while the plan loads, rather than a guard at every call site. The
      // drawing tools are unusable until there is something to draw on, so the projection
      // only has to exist, not be meaningful.
      const boundingBox = plan
        ? computeBoundingBox(plan)
        : { minX: 0, minY: 0, maxX: 1, maxY: 1 };

      return createProjection(boundingBox, SURFACE_WIDTH, SURFACE_HEIGHT);
    },
    [editorDocument.floorPlan],
  );

  /**
   * Selects a feature and brings it into view.
   *
   * Framed rather than merely centred: a desk found by id at the current zoom is still a
   * four-pixel dot somewhere on a floor plate, and "found it" has to mean the admin can see
   * which one it is.
   */
  const goTo = useCallback((feature: EditorFeature): void => {
    selection.selectKeys([selectionKeyOf(feature)]);

    const bounds = boundsOf([feature], projection);

    if (bounds) {
      viewport.frame(bounds);
    }
  }, [selection, projection, viewport]);

  const { setFitBox, fitToView } = viewport;

  // What fit-to-view should fit: the plan's own extent on the surface, not the whole surface.
  // Followed on every edit, so the button stays correct as the drawing grows.
  useEffect(() => {
    setFitBox(projectedExtentOf(projection));
  }, [projection, setFitBox]);

  // The view itself is only re-framed when a *different* plan is opened. Tying this to the
  // projection meant it fired on every edit — the projection is rebuilt from a new document
  // object after every drag frame — so zooming into a corner to draw a wall animated the
  // camera straight back out from under the person drawing.
  const openPlanId = editorDocument.floorPlan?.floorPlanId;
  const openStorey = editorDocument.floorPlan?.storey;

  useEffect(() => {
    fitToView();
  }, [openPlanId, openStorey, fitToView]);

  const drawing = useDrawing({
    tool,
    target: drawTarget,
    projection,
    formatLength: (units): string => formatLength(units, scale),
    formatArea: (squareUnits): string => formatArea(squareUnits, scale),
    onPolygonDrawn: (coordinates, target): void => {
      const created = editorDocument.createPolygon(coordinates, target);

      if (created) {
        selection.selectKeys([created]);
      }

      // Back to the pointer once a shape lands, so the panel shows what can be done with it.
      // The drawing rail keeps a tool armed for the next shape; somebody new to this expects
      // the shape they just drew to be the thing now in their hands.
      if (!EDITOR_FEATURES.legacyToolRail) {
        setTool('select');
      }
    },
    onLineDrawn: (from, to): void => {
      // Drawn to a real thickness where the plan has a scale, and to a sliver of the plan's
      // width where it has none — visible and clickable, with no metre figure to pretend to.
      const metres = isUsableScale(scale) ? DEFAULT_LINE_THICKNESS_METRES : null;
      const units = metres !== null ? metresToUnits(metres, scale) : null;
      const created = editorDocument.createLine(from, to, units ?? planWidth / 400, metres);

      if (created) {
        selection.selectKeys([created]);
      }

      if (!EDITOR_FEATURES.legacyToolRail) {
        setTool('select');
      }
    },
  });

  /**
   * Switches what is being worked on.
   *
   * Drops whatever was half-drawn and anything selected that the new mode cannot reach — a
   * room left selected in Border mode would be edited by a panel that is not showing it.
   */
  const changeMode = useCallback((next: EditMode): void => {
    drawing.onCancel();
    setTool('select');
    setPendingIconName(undefined);
    setMode(next);
    // The outline is only ever reshaped, so its mode starts on corners rather than on a move.
    setReshape(next === 'border' ? 'corners' : 'off');

    // The building outline is picked up straight away: a floor has one, it is what Border mode
    // is for, and making somebody find and click the edge of the whole plan first is a step
    // that only exists to be got wrong.
    if (next === 'border' && editorDocument.floorPlan?.border.length) {
      // Shown first if it was hidden, or the outline would be selected and edited unseen —
      // and hit-testing ignores hidden shapes, so it could not even be clicked away from.
      setSoloLayer((current) => (current !== null && current !== 'border' ? null : current));
      setLayerSettings((current) => withVisible(current, 'border', true));
      selection.selectKeys(editorDocument.floorPlan.border.map(selectionKeyOf));
      return;
    }

    const reachable = selection.features.filter((feature) => acceptsFeature(next, feature));

    if (reachable.length !== selection.features.length) {
      selection.selectKeys(reachable.map(selectionKeyOf));
    }
  }, [drawing, selection, editorDocument.floorPlan]);

  /** Arms a drawing tool from the panel or the shapes tray, landing on the given layer. */
  const startDrawing = useCallback((next: EditorTool, target: DrawTarget): void => {
    drawing.onCancel();
    setDrawTarget(target);
    setTool(next);
  }, [drawing]);

  const startShape = useCallback((shape: BasicShapeTool): void => {
    // Basic shapes go on the floor, so the Floor mode comes with them — which is also what
    // makes the shape editable once it has landed.
    if (mode !== 'floor') {
      changeMode('floor');
    }

    startDrawing(shape, 'floor');
  }, [mode, changeMode, startDrawing]);

  /**
   * Arms a tool, the way the rail and the keyboard both do.
   *
   * The icon and desk-group tools open their pickers with them, because what gets placed is
   * the whole of what those tools do. A drawing tool picked by its letter lands on the layer
   * the current mode edits, so pressing R in Rooms draws a room and in Floor draws a shape.
   */
  const armTool = useCallback((next: EditorTool): void => {
    // Switching tools abandons whatever was half-drawn. Carrying a draft across is the only
    // alternative, and a polygon that becomes a wall halfway through is not something anybody
    // asked for.
    drawing.onCancel();

    if (isDrawingTool(next) && !EDITOR_FEATURES.legacyToolRail) {
      setDrawTarget(polygonLayerOf(mode) ?? 'floor');
    }

    setTool(next);

    if (next === 'icon') {
      setIconPickerAnchor(toolRailRef.current);
    }

    if (next === 'deskGroup') {
      setGroupPickerAnchor(toolRailRef.current);
    }
  }, [drawing, mode]);

  // Only while nothing is armed: a drawing or placement tool reaches through everything, and
  // the mode's job is to say what a plain click edits.
  const acceptInMode = useMemo(
    () => (mode === 'select' ? undefined : (feature: EditorFeature): boolean => acceptsFeature(mode, feature)),
    [mode],
  );
  const dimmedLayers = useMemo(() => dimmedLayersOf(mode), [mode]);
  const canvasReshape: ReshapeMode = ((): ReshapeMode => {
    if (tool === 'vertex') return 'corners';
    return isAreaMode(mode) && tool === 'select' ? reshape : 'off';
  })();

  /**
   * Schedules a desk's withdrawal, then re-reads the rooms so the badge appears.
   *
   * Refreshed rather than patched into local state: the response carries the office's own view
   * of the window, and the assignments are where the editor reads retirement from — keeping a
   * second copy in component state is how the badge and the dialog end up disagreeing.
   */
  const scheduleRetirement = useCallback(async (
    workplaceId: string,
    retiresOn: string,
    allowInsideBookingWindow = false,
  ): Promise<boolean> => {
    const result = await retirement.schedule(workplaceId, retiresOn, allowInsideBookingWindow);

    // Reported back so the dialog knows whether to close. It must stay open on a refusal - that
    // is where the reason is shown, and closing would throw the message away along with the date
    // the administrator picked.
    if (!result) return false;

    // Green: the desk really is scheduled to go. notify() defaults to an error toast, and a red
    // one reads as "that failed" — which is the opposite of what happened.
    notify(String(t('floorplan-editor-retire-scheduled', {
      date: new Date(retiresOn).toLocaleDateString(),
    })), true);
    refreshRooms();

    return true;
  }, [retirement, refreshRooms, t]);

  const cancelRetirement = useCallback(async (workplaceId: string): Promise<void> => {
    const result = await retirement.cancel(workplaceId);

    if (!result) {
      // The one failure worth naming: the job applied the retirement between this page being read
      // and the button being pressed, so there is nothing left to cancel. The editor hides the
      // action for a desk it already knows is out of service, but the job runs on a timer and this
      // is the gap. Re-reading the rooms is what makes the button go away.
      if (retirement.error === 'alreadyApplied') {
        notify(String(t('floorplan-editor-retire-cancel-too-late')));
        refreshRooms();
      }

      return;
    }

    notify(String(t('floorplan-editor-retire-cancelled')), true);
    refreshRooms();
  }, [retirement, refreshRooms, t]);

  const bookableFeatureIds = useMemo(
    // A desk is bookable because a Workplaces row exists for it, in a room of this office.
    // That is what the assignments are — not simply "came back from the server": the stored
    // data already holds desk features with no workplace behind them at all.
    () => bookableFeatureIdsOf(assignments),
    [assignments],
  );

  // When the rooms could not be read, nothing is known about which desks are bookable — and
  // "unknown" must not be drawn as "not bookable". Marking every desk on the plan orange
  // because one request was refused is a far louder claim than the truth, which is that the
  // editor could not find out.
  const unbookableDeskIds = useMemo(
    () => (roomsError
      ? []
      : (editorDocument.floorPlan?.desks ?? [])
        .filter((desk) => !bookableFeatureIds.has(desk.featureId))
        .map((desk) => desk.featureId)),
    [roomsError, editorDocument.floorPlan, bookableFeatureIds],
  );

  const deleteSelected = useCallback((): void => {
    // A stamped desk is two features: the marker that carries the booking and the outline drawn
    // under it. Deleting only the marker left the outline behind — on one desk a stray shape, on
    // a group it read as the delete having done nothing, since every desk image stayed put.
    const { floorPlan } = editorDocument;
    const keys = new Set(selection.keys);

    if (floorPlan) {
      const desks = floorPlan.desks.filter((desk) => keys.has(selectionKeyOf(desk)));

      outlinesOfDesks(floorPlan, desks, scale).forEach((outline) => keys.add(selectionKeyOf(outline)));
    }

    editorDocument.remove(keys);
    selection.clear();
    setPendingDeletion(undefined);
  }, [editorDocument, selection, scale]);

  /**
   * Deletes the selection, asking first when a room would lose capacity by it.
   *
   * Only bookable desks cost a room a seat, so deleting a label, a desk shape or a desk that
   * was never bookable goes straight through. A dialog on every delete would train the admin
   * to dismiss it unread.
   */
  const requestDelete = useCallback((): void => {
    // The building outline is reshaped, never removed: a floor has exactly one, and without it
    // nothing says where the building ends. Refused here rather than only hidden in the Border
    // panel, because the Delete key, the right-click menu and the Select mode inspector all end
    // up here too.
    if (!EDITOR_FEATURES.legacyToolRail
      && selection.features.some((feature) => feature.kind === 'polygon' && feature.layer === 'border')) {
      notify(String(t('floorplan-simple-border-no-delete')));
      return;
    }

    // Refused for more than one bookable desk, however the delete was reached — the Delete
    // key included, which is the way a forty-desk rubber-band selection gets wiped by
    // accident. The inspector's button is disabled for the same case, but a guard only on the
    // button would leave the keyboard doing what the button refuses.
    if (!canDeleteSelection(selection.features, bookableFeatureIds)) {
      notify(String(t('floorplan-editor-delete-bulk-refused', {
        count: bookableDeskCountOf(selection.features, bookableFeatureIds),
      })));
      return;
    }

    const impacts = capacityImpactOf({
      features: selection.features,
      rooms,
      bookableFeatureIds,
      // Room capacities were read when the plan was opened, so edits made since have to be
      // counted in — otherwise deleting from the same room twice reports the same numbers
      // both times and the save lands somewhere the admin was never shown.
      pendingDeltas: pendingCapacityDeltas(
        floorPlanWithRooms?.desks ?? [],
        editorDocument.floorPlan?.desks ?? [],
        bookableFeatureIds,
      ),
    });

    if (impacts.length === 0) {
      deleteSelected();
      return;
    }

    setPendingDeletion(impacts);
  }, [
    selection.features,
    rooms,
    bookableFeatureIds,
    deleteSelected,
    floorPlanWithRooms,
    editorDocument.floorPlan,
    t,
  ]);

  // Declared after `requestDelete` so the Delete key can be routed through it. Both are
  // unconditional hook calls and every early return is below them, so the hook order is
  // the same on every render.
  useNudge({
    document: editorDocument,
    selection,
    planWidth,
    // Not in Border mode: the outline cannot be dragged there, and the arrow keys moving the
    // whole building a step at a time would be a way round that.
    enabled: !isPlacementTool(tool) && mode !== 'border',
    onDelete: requestDelete,
  });

  /**
   * Everything the editor can do, by name.
   *
   * One list, built once, feeding both the palette and the context menu — so a command
   * cannot exist in one and not the other, and neither can drift from what the rail does.
   * Each entry calls the same handler the control calls; nothing here is a second
   * implementation of an action.
   */
  const commands = useMemo<EditorCommand[]>(() => {
    const hasSelection = selection.features.length > 0;
    const areaCount = selection.features.filter((feature) => feature.kind === 'polygon').length;

    const toolCommands: EditorCommand[] = ([
      ['select', 'floorplan-editor-tool-select', 'V'],
      ['desk', 'floorplan-editor-tool-desk', 'D'],
      ['deskShape', 'floorplan-editor-tool-desk-shape', 'S'],
      ['text', 'floorplan-editor-tool-text', 'T'],
      ['icon', 'floorplan-editor-tool-icon', 'I'],
      ['polygon', 'floorplan-editor-tool-polygon', 'P'],
      ['rectangle', 'floorplan-editor-tool-rectangle', 'R'],
      ['ellipse', 'floorplan-editor-tool-ellipse', 'C'],
    ] as ReadonlyArray<[EditorTool, string, string | undefined]>).map(([value, labelKey, shortcut]) => ({
      id: `tool-${value}`,
      labelKey,
      group: 'tools' as const,
      shortcut,
      // A desk group laid out on a plan with no scale has no dimensions to be laid out at, so
      // the tool is offered but refused rather than silently stamping something microscopic.
      disabled: needsScale(value) && scale === null,
      run: (): void => {
        drawing.onCancel();
        setTool(value);
      },
    }));

    const arrangeEntries = arrangeCommands({
      alignable,
      selectedCount: selection.features.length,
      onAlign: align,
      onDistribute: distribute,
    });

    return [
      ...toolCommands,
      ...arrangeEntries,
      {
        id: 'duplicate',
        labelKey: 'floorplan-editor-duplicate',
        group: 'edit',
        shortcut: 'Ctrl/⌘ + D',
        disabled: !hasSelection,
        run: (): void => duplicate([duplicateOffsetOf(selection.features)]),
      },
      {
        id: 'array',
        labelKey: 'floorplan-editor-array',
        group: 'edit',
        disabled: !hasSelection,
        run: (): void => setArrayOpen(true),
      },
      {
        id: 'move-to-layer-room',
        labelKey: 'floorplan-editor-move-to-rooms',
        group: 'edit',
        disabled: areaCount === 0,
        run: (): void => editorDocument.moveToLayer(selection.keys, 'room'),
      },
      {
        id: 'move-to-layer-floor',
        labelKey: 'floorplan-editor-move-to-floor',
        group: 'edit',
        disabled: areaCount === 0,
        run: (): void => editorDocument.moveToLayer(selection.keys, 'floor'),
      },
      {
        id: 'move-to-layer-border',
        labelKey: 'floorplan-editor-move-to-border',
        group: 'edit',
        disabled: areaCount === 0,
        run: (): void => editorDocument.moveToLayer(selection.keys, 'border'),
      },
      {
        id: 'delete',
        labelKey: 'floorplan-editor-shortcut-delete',
        group: 'edit',
        shortcut: 'Delete',
        disabled: !hasSelection,
        run: requestDelete,
      },
      {
        id: 'select-all',
        labelKey: 'floorplan-editor-shortcut-select-all',
        group: 'edit',
        shortcut: 'Ctrl/⌘ + A',
        run: selection.selectAll,
      },
      {
        id: 'undo',
        labelKey: 'floorplan-editor-undo',
        group: 'edit',
        shortcut: 'Ctrl/⌘ + Z',
        disabled: !editorDocument.canUndo,
        run: editorDocument.undo,
      },
      {
        id: 'redo',
        labelKey: 'floorplan-editor-redo',
        group: 'edit',
        shortcut: 'Ctrl/⌘ + ⇧ + Z',
        disabled: !editorDocument.canRedo,
        run: editorDocument.redo,
      },
      {
        id: 'zoom-in',
        labelKey: 'floorplan-editor-zoom-in',
        group: 'view',
        run: viewport.zoomIn,
      },
      {
        id: 'zoom-out',
        labelKey: 'floorplan-editor-zoom-out',
        group: 'view',
        run: viewport.zoomOut,
      },
      {
        id: 'fit-to-view',
        labelKey: 'floorplan-editor-fit-to-view',
        group: 'view',
        run: viewport.fitToView,
      },
      {
        id: 'toggle-grid',
        labelKey: 'floorplan-editor-grid-lines',
        group: 'view',
        shortcut: 'G',
        run: (): void => setShowGrid((current) => !current),
      },
      {
        id: 'show-all-layers',
        labelKey: 'floorplan-editor-show-all-layers',
        group: 'view',
        disabled: !layersFiltered,
        run: (): void => {
          setLayerSettings(DEFAULT_LAYER_SETTINGS);
          setSoloLayer(null);
        },
      },
      {
        id: 'find',
        labelKey: 'floorplan-editor-panel-find',
        group: 'view',
        shortcut: 'Ctrl/⌘ + F',
        run: (): void => {
          setRailCollapsed(false);
          setLeftTab('find');
        },
      },
      {
        id: 'import',
        labelKey: 'floorplan-editor-panel-import',
        group: 'plan',
        run: (): void => {
          setRailCollapsed(false);
          setLeftTab('import');
        },
      },
      {
        id: 'automate',
        labelKey: 'floorplan-editor-panel-automate',
        group: 'plan',
        run: (): void => {
          setRailCollapsed(false);
          setLeftTab('automate');
        },
      },
      {
        id: 'validate',
        labelKey: 'floorplan-editor-panel-validate',
        group: 'plan',
        run: (): void => {
          setRailCollapsed(false);
          setLeftTab('validate');
        },
      },
      {
        id: 'history',
        labelKey: 'floorplan-editor-panel-history',
        group: 'view',
        run: (): void => {
          setRailCollapsed(false);
          setLeftTab('history');
        },
      },
      {
        id: 'save',
        labelKey: 'floorplan-editor-save-draft',
        group: 'plan',
        shortcut: 'Ctrl/⌘ + S',
        disabled: saving,
        run: saveDraft,
      },
      {
        id: 'publish',
        labelKey: 'floorplan-editor-publish',
        group: 'plan',
        disabled: saving,
        run: save,
      },
      {
        id: 'export',
        labelKey: 'floorplan-editor-export',
        group: 'plan',
        run: (): void => guardUnsaved('floorplan-editor-export-unsaved-warning', () => exportPlan()),
      },
      {
        id: 'shortcuts',
        labelKey: 'floorplan-editor-shortcuts',
        group: 'plan',
        shortcut: '?',
        run: (): void => setShortcutsOpen(true),
      },
    ];
  }, [
    selection,
    scale,
    drawing,
    align,
    alignable,
    distribute,
    duplicate,
    editorDocument,
    requestDelete,
    viewport,
    layersFiltered,
    saving,
    save,
    saveDraft,
    exportPlan,
    guardUnsaved,
  ]);

  /**
   * The short list a right-click offers.
   *
   * Drawn from the same registry rather than written out again: a context menu with its own
   * copy of "delete" is a second delete to keep in step with the guard rails on the first.
   */
  const contextCommands = useMemo(() => {
    // The alignment entries drop out on their own for a desk: they are not in the registry for
    // a selection that cannot be aligned, and this list keeps only the ids it finds.
    // The short menu while the advanced tools are off: what a right-click offers should not
    // be a way round the toolbox into features that have not been rolled out yet.
    const advanced = EDITOR_FEATURES.bulkActions;
    const idsFor = contextMenu?.feature
      ? ['duplicate', 'delete', ...(advanced
        ? ['array', 'align-left', 'align-top', 'move-to-layer-room', 'move-to-layer-floor']
        : [])]
      : ['select-all', 'fit-to-view', ...(EDITOR_FEATURES.sidePanels ? ['show-all-layers', 'find'] : [])];

    return idsFor
      .map((id) => commands.find((command) => command.id === id))
      .filter((command): command is EditorCommand => command !== undefined);
  }, [commands, contextMenu]);

  useEditorShortcuts({
    document: editorDocument,
    selection,
    onToolChange: armTool,
    // Ctrl+S keeps the work, it does not publish it: putting a floor in front of everybody who
    // books from it is a decision, made with the Publish button.
    onSave: saveDraft,
    onShowShortcuts: (): void => setShortcutsOpen(true),
    onCompleteDrawing: drawing.onComplete,
    onCancelDrawing: (): boolean => {
      if (!drawing.isDrawing) {
        return false;
      }

      drawing.onCancel();
      return true;
    },
    onToggleGrid: (): void => setShowGrid((current) => !current),
    onDuplicate: (): void => duplicate([duplicateOffsetOf(selection.features)]),
    onShowCommands: EDITOR_FEATURES.commandPalette ? (): void => setPaletteOpen(true) : undefined,
    onFind: EDITOR_FEATURES.commandPalette && EDITOR_FEATURES.sidePanels
      ? (): void => {
        setRailCollapsed(false);
        setLeftTab('find');
      }
      : undefined,
  });

  // Only while there is nothing to show. A refresh after a save must not replace the whole
  // editor with a spinner for as long as the fetch takes.
  if ((loading || roomsLoading) && !editorDocument.floorPlan) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '60vh' }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{t('floorplan-editor-load-failed')}</Alert>;
  }

  // An office with no plan for this storey. Offering the import here rather than an
  // information banner: this is the page an administrator navigates to in order to make one,
  // and the upload used to live behind a menu item on a different screen.
  if (!editorDocument.floorPlan || startingFloor !== undefined) {
    return (
      <FloorPlanStarter
        key={startingFloor ?? 'first'}
        officeId={officeId ?? ''}
        initialStorey={startingFloor}
        existingStoreys={storeys}
        onCancel={editorDocument.floorPlan ? (): void => setStartingFloor(undefined) : undefined}
        onCreated={(created): void => {
          setStartingFloor(undefined);
          setStorey(created);
          refreshFloorPlan();
          notify(String(t('floorplan-editor-create-saved')), true);
        }}
      />
    );
  }

  return (
    <Stack
      sx={{
        // The page gives its children an absolutely positioned box the size of the viewport
        // beside the menu, so the editor takes all of it. A fixed `100vh - 120px` left a strip
        // of page background under the editor on every screen, which is height the canvas
        // could have had.
        height: '100%',
        boxSizing: 'border-box',
        // The app sets `color: white` on the body for the dashboard's dark cards, so every
        // page has to state its own text colour or it renders white on white. The editor is
        // a working surface rather than a card, so it opts out for its whole subtree.
        color: editorTheme.ink,
        backgroundColor: editorTheme.surface,
        borderRadius: `${editorTheme.radius}px`,
        border: `1px solid ${editorTheme.line}`,
        overflow: 'hidden',
        boxShadow: editorTheme.shadowPanel,
      }}
    >
      <EditorTopBar
        officeName={officeName}
        storey={editorDocument.floorPlan.storey}
        storeys={storeys}
        dirty={editorDocument.dirty}
        saving={saving}
        canUndo={editorDocument.canUndo}
        canRedo={editorDocument.canRedo}
        onBack={(): void => guardUnsaved('floorplan-editor-discard-changes', () => navigate(-1))}
        onStoreyChange={(next): void => guardUnsaved('floorplan-editor-discard-changes', () => {
          setStorey(next);
          selection.clear();
        })}
        onUndo={editorDocument.undo}
        onRedo={editorDocument.redo}
        onSave={save}
        onExport={(): void => guardUnsaved('floorplan-editor-export-unsaved-warning', () => {
          exportPlan();
        })}
        onShowShortcuts={(): void => setShortcutsOpen(true)}
        hasDraft={!!draftState.draft}
        unpublished={editorDocument.floorPlan.publishedAt === null}
        onSaveDraft={saveDraft}
        onSchedule={(): void => setScheduleOpen(true)}
        onRemove={(): void => guardUnsaved('floorplan-editor-discard-changes', () => setRemoveOpen(true))}
        onAddFloor={(): void => guardUnsaved('floorplan-editor-discard-changes', () => {
          setStartingFloor(storeys.length ? Math.max(...storeys) + 1 : 0);
        })}
        // The plan is read again under its new number, so unsaved edits would not survive it.
        onChangeStorey={(): void => guardUnsaved('floorplan-editor-change-storey-unsaved', () => setChangeStoreyOpen(true))}
      />

      {draftState.draft && (
        <DraftBanner
          draft={draftState.draft}
          busy={draftBusy || saving}
          onCancelSchedule={(): void => {
            cancelSchedule().catch(() => undefined);
          }}
          onDiscard={(): void => setPendingConfirmation({
            messageKey: 'floorplan-editor-draft-discard-confirm',
            act: () => {
              discardDraft().catch(() => undefined);
            },
          })}
        />
      )}

      {draftState.failed && (
        <Alert severity="warning" sx={{ m: 1 }}>{t('floorplan-editor-draft-unavailable')}</Alert>
      )}

      {roomsError && (
        <Alert severity={roomsError === 'forbidden' ? 'warning' : 'error'} sx={{ m: 1 }}>
          {t(roomsError === 'forbidden'
            ? 'floorplan-editor-rooms-forbidden'
            : 'floorplan-editor-rooms-unavailable')}
        </Alert>
      )}

      {issues.length > 0 && (
        <Alert severity={issues.some((issue) => issue.severity === 'error') ? 'error' : 'warning'} sx={{ m: 1 }}>
          <Stack>
            {issues.slice(0, 5).map((issue) => (
              <Typography key={`${issue.key}-${issue.messageKey}`} variant="caption">
                {String(t(issue.messageKey))}
                {issue.context ? ` (${issue.context})` : ''}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      <Stack direction="row" sx={{ flexGrow: 1, minHeight: 0, flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
        {EDITOR_FEATURES.sidePanels && (
          <EditorLeftRail
            tab={leftTab}
            onTabChange={setLeftTab}
            collapsed={railCollapsed}
            onCollapsedChange={setRailCollapsed}
          >
            {leftTab === 'layers' && (
              <LayersPanel
                settings={layerSettings}
                solo={soloLayer}
                onVisibleChange={(key, visible): void => setLayerSettings(
                  (current) => withVisible(current, key, visible),
                )}
                onSoloChange={(key): void => setSoloLayer((current) => nextSolo(current, key))}
                onSelectLayer={selectLayer}
                retiredDeskCount={retiredDeskIds.size}
              />
            )}

            {leftTab === 'import' && (
              <ImportPanel
                officeId={editorDocument.floorPlan.officeId || (officeId ?? '')}
                floorPlan={editorDocument.floorPlan}
                onImported={(next): void => {
                  editorDocument.replaceDrawing(next);
                  selection.clear();
                  notify(String(t('floorplan-editor-import-applied')), true);
                }}
              />
            )}

            {leftTab === 'history' && (
              <HistoryPanel
                entries={editorDocument.history.entries}
                index={editorDocument.history.index}
                onJumpTo={editorDocument.history.jumpTo}
              />
            )}

            {leftTab === 'find' && (
              <FindPanel floorPlan={editorDocument.floorPlan} onGoTo={goTo} />
            )}

            {leftTab === 'automate' && (
              <AutomatePanel
                suggestions={automation.suggestions}
                cleaning={automation.cleaning}
                busy={automation.busy}
                failed={automation.error}
                dirty={editorDocument.dirty}
                onRun={(): void => {
                  automation.runSuggestions().catch(() => undefined);
                }}
                onPlaceDesks={placeDetectedDesks}
                onDrawRooms={drawSuggestedRooms}
                onRelink={applyRelink}
                onPreviewCleaning={(): void => {
                  automation.previewCleaning().catch(() => undefined);
                }}
                onApplyCleaning={applyCleaning}
              />
            )}

            {leftTab === 'validate' && (
              <ValidatePanel
                validation={automation.validation}
                busy={automation.busy}
                failed={automation.error}
                dirty={editorDocument.dirty}
                onRun={(): void => {
                  automation.runValidation().catch(() => undefined);
                }}
              />
            )}

          </EditorLeftRail>
        )}

        {/* The toolbox is a column of its own at the left of the plan rather than a slab
            floating over it: its buttons carry their names, which makes it too wide to sit on
            the drawing without covering the part of the plan somebody is trying to click. */}
        {!EDITOR_FEATURES.legacyToolRail && (
          <Box
            ref={toolRailRef}
            sx={{
              flexShrink: 0,
              py: 1,
              px: 0.5,
              overflowY: 'auto',
              borderRight: `1px solid ${editorTheme.line}`,
              backgroundColor: editorTheme.surface,
            }}
          >
            <SimpleToolbox
              docked
              drawTarget={drawTarget}
              mode={mode}
              tool={tool}
              onModeChange={changeMode}
              onShapeTool={startShape}
              showGrid={showGrid}
              onShowGridChange={setShowGrid}
            />
          </Box>
        )}

        <Stack sx={{ flexGrow: 1, minWidth: 0, minHeight: 420 }}>
          <Box
            sx={{
              position: 'relative',
              flexGrow: 1,
              minHeight: 0,
              backgroundColor: editorTheme.workspace,
              overflow: 'hidden',
            }}
          >
            <FloorplanCanvas
              floorPlan={editorDocument.floorPlan}
              viewport={viewport}
              width={SURFACE_WIDTH}
              height={SURFACE_HEIGHT}
              layerVisibility={layerVisibility}
              selection={selection}
              document={editorDocument}
              onPlace={isPlacementTool(tool) && (tool !== 'icon' || pendingIconName) ? place : undefined}
              onDraw={isDrawingTool(tool) ? drawing.onClick : undefined}
              onDrawMove={isDrawingTool(tool) ? drawing.onMove : undefined}
              draft={drawing.draft}
              draftReadout={drawing.readout}
              showGrid={showGrid}
              unitsPerMetre={scale}
              unbookableDeskIds={unbookableDeskIds}
              retiredDeskIds={retiredDeskIds}
              reshape={canvasReshape}
              accept={tool === 'select' ? acceptInMode : undefined}
              // Nothing moves whole while its corners or sides are being worked on, and the outline
              // never does: a press aimed at a corner that misses should not drag the building.
              lockMove={mode === 'border' || (isAreaMode(mode) && reshape !== 'off')}
              dimmedLayers={dimmedLayers}
              idleCursor={tool === 'select' ? modeCursorOf(mode) : undefined}
              // Only while the switches are rolled out: without them there is no way to turn
            // snapping off again, so it must not be able to be on.
              snapSettings={EDITOR_FEATURES.snapping ? snapSettings : undefined}
              snapGridSpacing={gridSpacingOf(scale, planWidth)}
              onPointerAt={reportPointer}
              onContextMenu={(position, feature): void => setContextMenu({ position, feature })}
            />

            {/* The drawing rail floats over the plan, as it always did: it is icon-only and
                narrow enough to. */}
            {EDITOR_FEATURES.legacyToolRail && (
              <Box ref={toolRailRef} sx={{ position: 'absolute', top: 12, left: 12, zIndex: 2 }}>
                <EditorToolRail
                  tool={tool}
                  hasScale={scale !== null}
                  showGrid={showGrid}
                  onShowGridChange={setShowGrid}
                  onToolChange={armTool}
                />
              </Box>
            )}

            {/* Nothing drawn yet. Without this the stage is an empty grey rectangle, which is
                also what a floorplan that failed to load looks like — and the two need telling
                apart before anybody starts hunting for a bug that is not there. */}
            {planIsEmpty && (
              <Stack
                spacing={0.5}
                alignItems="center"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  color: editorTheme.inkFaint,
                  textAlign: 'center',
                  px: 4,
                }}
              >
                <Typography variant="subtitle2" sx={{ color: editorTheme.inkDim }}>
                  {t('floorplan-editor-empty-title')}
                </Typography>
                <Typography variant="caption" sx={{ maxWidth: 380 }}>
                  {t('floorplan-editor-empty-hint')}
                </Typography>
              </Stack>
            )}

            <EditorZoomBar
              zoom={viewport.zoom}
              onZoomIn={viewport.zoomIn}
              onZoomOut={viewport.zoomOut}
              onFitToView={viewport.fitToView}
            />

            <Minimap
              floorPlan={editorDocument.floorPlan}
              surfaceWidth={SURFACE_WIDTH}
              surfaceHeight={SURFACE_HEIGHT}
              viewBox={viewport.viewBox}
              layerVisibility={layerVisibility}
              onCentreOn={viewport.centreOn}
            />
          </Box>

          {/* Which layers are drawn, always in view along the bottom of the plan. Show and hide
              only: solo and select-a-whole-layer stay with the advanced side panel. */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              px: 1.5,
              py: 0.25,
              minHeight: 40,
              borderTop: `1px solid ${editorTheme.line}`,
              backgroundColor: editorTheme.surface,
              overflowX: 'auto',
            }}
            role="group"
            aria-label={String(t('floorplan-editor-panel-layers'))}
          >
            <LayersOutlineIcon size={18} style={{ flexShrink: 0, color: editorTheme.inkFaint }} />
            <Typography sx={{ ...sectionHeadingSx, flexShrink: 0 }}>{t('floorplan-editor-panel-layers')}</Typography>
            <LayersPanel
              orientation="horizontal"
              settings={layerSettings}
              solo={soloLayer}
              onVisibleChange={(key, visible): void => {
                setSoloLayer(null);
                setLayerSettings((current) => withVisible(current, key, visible));
              }}
              retiredDeskCount={retiredDeskIds.size}
            />

            {EDITOR_FEATURES.snapping && (
              <>
                <Box sx={{ flexGrow: 1 }} />

                <SnapControls
                  settings={snapSettings}
                  onChange={setSnapSettings}
                  gridLabel={formatLength(gridSpacingOf(scale, planWidth), scale)}
                />
              </>
            )}
          </Stack>
        </Stack>

        <Box
          sx={{
            width: { xs: '100%', lg: 320 },
            flexShrink: 0,
            borderLeft: { lg: `1px solid ${editorTheme.line}` },
            backgroundColor: editorTheme.surface,
            overflowY: 'auto',
          }}
        >
          {EDITOR_FEATURES.bulkActions && (
            <BulkActions
              selectedCount={selection.features.length}
              areaCount={selection.features.filter((feature) => feature.kind === 'polygon').length}
              alignable={alignable}
              onAlign={align}
              onDistribute={distribute}
              onDuplicate={(): void => duplicate([duplicateOffsetOf(selection.features)])}
              onArray={(): void => setArrayOpen(true)}
              onMoveToLayer={(target): void => editorDocument.moveToLayer(selection.keys, target)}
            />
          )}

          <ModePanel
            mode={mode}
            tool={tool}
            selection={selection}
            document={editorDocument}
            scale={scale}
            reshape={reshape}
            onReshapeChange={setReshape}
            rooms={rooms}
            storey={editorDocument.floorPlan.storey}
            roomIdsOnThisFloor={roomIdsOnThisFloor}
            roomsUnavailable={roomsError !== undefined}
            onDraw={startDrawing}
            onPlace={armTool}
            onCancelTool={(): void => {
              drawing.onCancel();
              setTool('select');
              setPendingIconName(undefined);
            }}
            onDuplicate={(): void => duplicate([duplicateOffsetOf(selection.features)])}
            onDelete={requestDelete}
            hasScale={scale !== null}
          >
            <EditorInspector
              selection={selection}
              document={editorDocument}
              scale={scale}
              bookableFeatureIds={bookableFeatureIds}
              retirementDates={retirementDates}
              appliedRetirementIds={appliedRetirementIds}
              earliestRetirementDate={earliestRetirementDate}
              planPeriodDays={planPeriodDays}
              retirementSaving={retirement.saving}
              retirementError={retirement.error}
              // Returns whether it worked, so the dialog can close itself on success and stay open
              // on a refusal - the refusal is shown inside it. A rejected promise is reported as a
              // failure rather than swallowed, for the same reason.
              //
              // Forwarded as a rest parameter, not re-listed. Naming the parameters here is how
              // the booking-window override was silently dropped once already: TypeScript accepts
              // a handler that takes fewer arguments than the prop declares, so the flag reached
              // this line and went no further, and the server answered 486 with the dialog
              // insisting the box was ticked.
              onScheduleRetirement={(...args): Promise<boolean> =>
                scheduleRetirement(...args).catch(() => false)}
              onCancelRetirement={(workplaceId): void => {
                cancelRetirement(workplaceId).catch(() => undefined);
              }}
              rooms={rooms}
              storey={editorDocument.floorPlan.storey}
              roomIdsOnThisFloor={roomIdsOnThisFloor}
              roomsUnavailable={roomsError !== undefined}
              onDelete={requestDelete}
            />
          </ModePanel>
        </Box>
      </Stack>

      <ShortcutsDialog open={shortcutsOpen} onClose={(): void => setShortcutsOpen(false)} />

      {EDITOR_FEATURES.commandPalette && (
        <CommandPalette
          open={paletteOpen}
          commands={commands}
          onClose={(): void => setPaletteOpen(false)}
        />
      )}

      <CanvasContextMenu
        position={contextMenu?.position}
        commands={contextCommands}
        subjectLabel={contextMenu?.feature
          ? String(t('floorplan-editor-selected-count', { count: selection.features.length }))
          : undefined}
        onClose={(): void => setContextMenu(undefined)}
      />

      <ArrayDialog
        open={arrayOpen}
        scale={scale}
        onConfirm={(spec: ArraySpec): void => {
          duplicate(arrayOffsets(spec));
          setArrayOpen(false);
        }}
        onCancel={(): void => setArrayOpen(false)}
      />

      <SchedulePublishDialog
        open={scheduleOpen}
        busy={saving}
        onCancel={(): void => setScheduleOpen(false)}
        onSchedule={(publishAt): void => {
          schedule(publishAt)
            .then((scheduled) => {
              if (scheduled) setScheduleOpen(false);
            })
            .catch(() => undefined);
        }}
      />

      <ChangeStoreyDialog
        open={changeStoreyOpen}
        floorPlanId={liveFloorPlan?.floorPlanId}
        storey={editorDocument.floorPlan.storey}
        storeys={storeys}
        onCancel={(): void => setChangeStoreyOpen(false)}
        onChanged={(changed): void => {
          setChangeStoreyOpen(false);
          selection.clear();
          notify(String(t('floorplan-editor-change-storey-done', { storey: changed })), true);
          // The same plan, found under its new number.
          setStorey(changed);
          refreshFloorPlan();
          refreshRooms();
        }}
      />

      <RemoveFloorPlanDialog
        open={removeOpen}
        floorPlanId={liveFloorPlan?.floorPlanId}
        onCancel={(): void => setRemoveOpen(false)}
        onRemoved={(removedStorey): void => {
          setRemoveOpen(false);
          selection.clear();
          notify(String(t('floorplan-editor-removed')), true);
          // Straight back to the choice of how to start this floor again.
          setStartingFloor(removedStorey);
          refreshFloorPlan();
          refreshRooms();
        }}
      />

      <ConfirmDialog
        open={pendingConfirmation !== undefined}
        messageKey={pendingConfirmation?.messageKey ?? ''}
        onConfirm={(): void => {
          pendingConfirmation?.act();
          setPendingConfirmation(undefined);
        }}
        onCancel={(): void => setPendingConfirmation(undefined)}
      />

      <DeleteDesksDialog
        open={pendingDeletion !== undefined}
        impacts={pendingDeletion ?? []}
        otherCount={selection.features.length - (pendingDeletion ?? []).reduce(
          (total, impact) => total + impact.deskCount,
          0,
        )}
        onConfirm={deleteSelected}
        onCancel={(): void => setPendingDeletion(undefined)}
      />

      <RoomPickerDialog
        open={pendingDeskAt !== undefined}
        rooms={rooms}
        storey={editorDocument.floorPlan.storey}
        roomIdsOnThisFloor={roomIdsOnThisFloor}
        roomsUnavailable={roomsError !== undefined}
        onConfirm={placeDeskInRoom}
        onCancel={(): void => setPendingDeskAt(undefined)}
      />

      <DeskGroupPicker
        anchorEl={groupPickerAnchor}
        scale={scale}
        kind={groupKind}
        deskIcons={deskIcons}
        onDeskIconsChange={setDeskIcons}
        custom={groupCustom}
        onCustomChange={setGroupCustom}
        footprint={groupFootprint}
        rotationDegrees={groupRotation}
        gapMetres={groupGap}
        aisleMetres={groupAisle}
        backGapMetres={groupBackGap}
        onBackGapChange={setGroupBackGap}
        onGapChange={setGroupGap}
        onAisleChange={setGroupAisle}
        onKindChange={setGroupKind}
        onFootprintChange={setGroupFootprint}
        onRotationChange={setGroupRotation}
        onClose={(): void => setGroupPickerAnchor(null)}
      />

      <RoomPickerDialog
        open={pendingGroupAt !== undefined}
        rooms={rooms}
        storey={editorDocument.floorPlan.storey}
        roomIdsOnThisFloor={roomIdsOnThisFloor}
        roomsUnavailable={roomsError !== undefined}
        onConfirm={placeDeskGroupInRoom}
        onCancel={(): void => setPendingGroupAt(undefined)}
      />

      <IconPicker
        anchorEl={iconPickerAnchor}
        onClose={(): void => {
          setIconPickerAnchor(null);

          // Closing without choosing leaves nothing to place, so the tool goes back rather
          // than sitting armed with no icon.
          if (!pendingIconName) {
            setTool('select');
          }
        }}
        onPick={(iconName): void => {
          setPendingIconName(iconName);
          setIconPickerAnchor(null);
        }}
      />

      <EditorStatusBar
        selectedCount={selection.features.length}
        pointerAt={pointerAt}
        planSummary={t('floorplan-editor-plan-summary', {
          desks: editorDocument.floorPlan.desks.length,
          shapes: editorDocument.floorPlan.deskShapes.length,
          labels: editorDocument.floorPlan.labels.length,
        })}
        layersFiltered={layersFiltered}
        onShowAllLayers={(): void => {
          setLayerSettings(DEFAULT_LAYER_SETTINGS);
          setSoloLayer(null);
        }}
        drawTarget={drawTarget}
        onDrawTargetChange={setDrawTarget}
        showDrawingOptions={EDITOR_FEATURES.legacyToolRail && isDrawingTool(tool)}
        onShowCommands={EDITOR_FEATURES.commandPalette ? (): void => setPaletteOpen(true) : undefined}
      />
    </Stack>
  );
};

export default FloorPlanEditor;
